/**
 * todo - resolve typing issues in another story
 */
import { defineStore, storeToRefs } from 'pinia';
import { useUrlLocationOperations } from '~/composables/useUrlLocationOperations';
import { useProductStore } from '~/stores/useProductStore';
import useReviewsStore from './useReviewsStore';
import useIsolatedStickyAtbStateStore from '~/modules/sticky-product-traits/stores/useIsolatedStickyAtbStateStore';

/**
 * This store handles navigation logic related to reviews, such as redirecting
 * the user to the "Write a Review" page or scrolling to the reviews section.
 */
const useReviewsRedirectStore = defineStore('reviewsRedirectStore', () => {
  const { redirectUrl } = useUrlLocationOperations();
  const { product } = storeToRefs(useProductStore());
  const { activeReviewProductId } = storeToRefs(useReviewsStore());
  const stickyAtbStore = useIsolatedStickyAtbStateStore();

  // Actions
  /**
   * Redirects the user to the "Write a Review" page for the current product.
   * It can pre-fill the star rating and passes the memberId if it's a consolidated
   * third-party variant product.
   *
   * @param {number} [rating] - The star rating to pre-fill.
   */
  const redirectToWriteReview = (rating) => {
    const { isConsolidatedThirdVariant = false } = product.value?.detail?.flags ?? {};

    redirectUrl('writeAReview', {
      ID: product.value?.id,
      ...(rating && { starRating: rating }),
      ...(isConsolidatedThirdVariant && activeReviewProductId.value && { memberId: activeReviewProductId.value }),
    });
  };

  /**
   * Scrolls the page smoothly to the main reviews section.
   *
   * @param {ScrollBehavior} [behavior='auto'] - The scroll behavior to use.
   */
  const scrollToReviews = (behavior: ScrollBehavior = 'auto') => {
    const reviewSection = document.getElementById('review-section');

    // State variables for tracking stability and user interruption
    let previousTop = reviewSection?.getBoundingClientRect().top;
    let stableChecks = 0;
    const STABLE_CHECKS_REQUIRED = 5;
    const POSITION_CHECK_INTERVAL = 200;

    // Cleanup function: removes event listeners and clears the interval
    const cleanup = () => {
      window.removeEventListener('wheel', cleanup);
      window.removeEventListener('touchmove', cleanup);
      window.removeEventListener('keydown', onKey);
      clearInterval(intervalId);
    };

    const onKey = (e) => {
      const scrollKeys = ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '];
      if (scrollKeys.includes(e.key)) cleanup();
    };

    // Checks if the element is still shifting on the screen
    const checkPosition = () => {
      const top = reviewSection?.getBoundingClientRect().top;

      // If the position changed by more than 1px (layout shift), re-trigger the scroll
      if (previousTop !== top) {
        reviewSection?.scrollIntoView({
          behavior,
          block: 'start',
        });
        stableChecks = 0;
      } else {
        stableChecks++;
      }
      previousTop = top;

      // If the position hasn't changed for 5 consecutive checks run cleanup function
      if (stableChecks >= STABLE_CHECKS_REQUIRED) {
        cleanup();
      }
    };

    // Listen for user actions to yield scroll control back to the user
    window.addEventListener('wheel', cleanup, { passive: true });
    window.addEventListener('touchmove', cleanup, { passive: true });
    window.addEventListener('keydown', onKey);

    // Start monitoring the position
    const intervalId = setInterval(checkPosition, POSITION_CHECK_INTERVAL);

    window.addEventListener(
      'scrollend',
      () => {
        stickyAtbStore.triggerForcedRef();
      },
      { once: true },
    );

    // First (immediate) scroll
    reviewSection?.scrollIntoView({
      behavior,
      block: 'start',
    });
  };

  return {
    // Actions
    redirectToWriteReview,
    scrollToReviews,
  };
});

export default useReviewsRedirectStore;
